import { Box, Container, Typography, Fab, Tooltip } from '@mui/material';
import ForecastTimelineContainer from '../components/timeline/ForecastTimelineContainer';
import { useEffect, useState, useMemo } from 'react';
// import NotificationBanner from '../components/common/NotificationBanner'; // Now using StatusAlert instead
import { useNotificationCheck } from '../hooks/useNotificationCheck';
import TimeNavigationControls from '../components/TimeNavigationControls';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { NoLocationsScreen } from '../components/common/NoLocationsScreen';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { FeedbackModal } from '../components/feedback/FeedbackModal';
import { useAirportDataAccess } from '../hooks/useAirportDataAccess';
import { useHourlyTimelineData } from '../hooks/timeline/useHourlyTimelineData';
import { useSharedTimelineData } from '../hooks/timeline/useSharedTimelineData';
import { UpdatingDataChip } from '../components/common/UpdatingDataChip';
import { useAirportStatuses } from '../hooks/map/useAirportStatuses';
import { useFogStatus } from '../hooks/map/useFogStatus';
import { ALL_AIRPORTS_DATA, ALL_NZ_LOCATIONS, ALL_UK_LOCATIONS, ALL_EU_LOCATIONS, SIDELINED_UK_LOCATIONS } from '../data/locations';
import { FogStatusCalculator } from '../services/FogStatusCalculator';

export default function TimelinePage() {
  // State for feedback modal
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  // Get locations the user has access to based on ReBAC
  const { locations: locationsToShow, loading: locationsLoading } = useAirportDataAccess();

  // Split locations into NZ, UK, and EU - memoize to prevent infinite re-fetching
  const nzLocations = useMemo(
    () => locationsToShow.filter(id => ALL_NZ_LOCATIONS.includes(id)),
    [locationsToShow]
  );
  const ukLocations = useMemo(
    () => locationsToShow.filter(id => ALL_UK_LOCATIONS.includes(id) || SIDELINED_UK_LOCATIONS.includes(id)),
    [locationsToShow]
  );
  const euLocations = useMemo(
    () => locationsToShow.filter(id => ALL_EU_LOCATIONS.includes(id)),
    [locationsToShow]
  );

  // State for custom now time
  const [customNow, setCustomNow] = useState<Date | null>(() => {
    const customNowParam = new URLSearchParams(window.location.search).get('now');
    if (customNowParam) {
      const date = new Date(customNowParam);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  });
  // State for showing controls, initialized from query param
  const [showControls, setShowControls] = useState(() => {
    const showControlsParam = new URLSearchParams(window.location.search).get('showControls');
    return showControlsParam === 'true';
  });
  
  // State for UTC time toggle - default to true (UTC time)
  const [useUTCTime, setUseUTCTime] = useState(true);

  // Use hourly timeline data hook for NZ locations
  const {
    timelineData: nzHourlyTimelineDataMap,
    loading: nzLoading,
    error: nzError,
    hasDataError: nzHasDataError,
    refresh: nzRefresh,
    lastUpdated: nzLastUpdated
  } = useHourlyTimelineData(nzLocations, customNow, 5);

  // Use shared timeline data hook for UK locations (extract hourly data)
  const {
    hourlyTimelineData: ukHourlyTimelineDataMap,
    loading: ukLoading,
    error: ukError,
    hasDataError: ukHasDataError,
    refresh: ukRefresh,
    lastUpdated: ukLastUpdated
  } = useSharedTimelineData(ukLocations, customNow, 5);

  // Use shared timeline data hook for EU locations (extract hourly data)
  const {
    hourlyTimelineData: euHourlyTimelineDataMap,
    loading: euLoading,
    error: euError,
    hasDataError: euHasDataError,
    refresh: euRefresh,
    lastUpdated: euLastUpdated
  } = useSharedTimelineData(euLocations, customNow, 5);

  // Combine UK+EU locations for ranking and rendering
  const ukEuLocations = useMemo(
    () => [...ukLocations, ...euLocations],
    [ukLocations, euLocations]
  );

  const ukEuTimelineDataMap = useMemo(
    () => ({ ...ukHourlyTimelineDataMap, ...euHourlyTimelineDataMap }),
    [ukHourlyTimelineDataMap, euHourlyTimelineDataMap]
  );

  // Combined status for UK+EU
  const ukEuLoading = ukLoading || euLoading;
  const ukEuError = ukError || euError;
  const ukEuHasDataError = ukHasDataError || euHasDataError;
  const ukEuLastUpdated = ukLastUpdated < euLastUpdated ? ukLastUpdated : euLastUpdated;
  const ukEuRefresh = async () => {
    await Promise.all([ukRefresh(), euRefresh()]);
  };

  // Combine timeline data maps for status calculations - memoize to prevent infinite re-renders
  const timelineDataMap = useMemo(
    () => ({ ...nzHourlyTimelineDataMap, ...ukEuTimelineDataMap }),
    [nzHourlyTimelineDataMap, ukEuTimelineDataMap]
  );
  const loading = nzLoading || ukEuLoading;
  const error = nzError || ukEuError;
  const lastUpdated = nzLastUpdated < ukEuLastUpdated ? nzLastUpdated : ukEuLastUpdated;
  const refresh = async () => {
    await Promise.all([nzRefresh(), ukEuRefresh()]);
  };

  // Calculate airport statuses and overall fog status (same as MapPage)
  const { airportColors, airportStatuses } = useAirportStatuses({
    airportTimelineData: timelineDataMap,
    dataAirports: locationsToShow
  });

  const fogStatus = useFogStatus(airportColors, locationsToShow);

  // Rank NZ airports by forecast severity - memoize to prevent re-renders
  const nzAirportsForRanking = useMemo(() =>
    nzLocations
      .map(airportId => {
        const airport = ALL_AIRPORTS_DATA.find(a => a.id === airportId);
        if (!airport || !airportStatuses[airportId]) return null;

        return {
          id: airport.id,
          code: airport.code,
          name: airport.name,
          status: airportStatuses[airportId]
        };
      })
      .filter(a => a !== null),
    [nzLocations, airportStatuses]
  );

  // Rank UK+EU airports by forecast severity (combined ranking) - memoize to prevent re-renders
  const ukEuAirportsForRanking = useMemo(() =>
    ukEuLocations
      .map(airportId => {
        const airport = ALL_AIRPORTS_DATA.find(a => a.id === airportId);
        if (!airport || !airportStatuses[airportId]) return null;

        return {
          id: airport.id,
          code: airport.code,
          name: airport.name,
          status: airportStatuses[airportId]
        };
      })
      .filter(a => a !== null),
    [ukEuLocations, airportStatuses]
  );

  // Rank airports using centralized method - memoize
  const nzRanked = useMemo(
    () => FogStatusCalculator.rankAirportsBySeverity(nzAirportsForRanking),
    [nzAirportsForRanking]
  );
  const ukEuRanked = useMemo(
    () => FogStatusCalculator.rankAirportsBySeverity(ukEuAirportsForRanking),
    [ukEuAirportsForRanking]
  );

  // Convert to timeline data in ranked order - memoize
  const nzTimelineData = useMemo(
    () => nzRanked.map(r => nzHourlyTimelineDataMap[r.id]).filter(data => data !== undefined),
    [nzRanked, nzHourlyTimelineDataMap]
  );

  const ukEuTimelineData = useMemo(
    () => ukEuRanked.map(r => ukEuTimelineDataMap[r.id]).filter(data => data !== undefined),
    [ukEuRanked, ukEuTimelineDataMap]
  );

  // Combined for notification check - memoize
  const timelineData = useMemo(
    () => [...nzTimelineData, ...ukEuTimelineData],
    [nzTimelineData, ukEuTimelineData]
  );

  // Update customNow and URL without reload
  const updateUrlWithTime = (newTime: Date | null, keepControls?: boolean) => {
    const url = new URL(window.location.href);
    if (newTime) {
      url.searchParams.set('now', newTime.toISOString());
      setCustomNow(newTime);
      if (keepControls || showControls) {
        url.searchParams.set('showControls', 'true');
        setShowControls(true);
      } else {
        url.searchParams.delete('showControls');
        setShowControls(false);
      }
    } else {
      url.searchParams.delete('now');
      url.searchParams.delete('showControls');
      setCustomNow(null);
      setShowControls(false);
    }
    window.history.pushState({}, '', url.toString());
  };

  // Keep customNow and showControls in sync with URL on browser navigation
  useEffect(() => {
    const handlePopState = () => {
      const customNowParam = new URLSearchParams(window.location.search).get('now');
      const showControlsParam = new URLSearchParams(window.location.search).get('showControls');
      if (customNowParam) {
        const date = new Date(customNowParam);
        setCustomNow(isNaN(date.getTime()) ? null : date);
      } else {
        setCustomNow(null);
      }
      setShowControls(showControlsParam === 'true');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Format the last updated timestamp
  const formatLastUpdated = (date: Date): string => {
    const timeString = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Get browser timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return `${timeString} (${timezone})`;
  };

  // Check for data quality issues (incomplete predictions, missing historical data, etc.)
  const notification = useNotificationCheck({
    timelineData,
    loading,
    hasCustomNow: !!customNow,
    error,
    checkInterval: 180000 // 3 minutes
  });

  // Convert notification to data quality warning format for StatusAlert
  // Note: We don't show these in NotificationBanner anymore, they're in StatusAlert below the divider
  const dataQualityWarning = notification.showNotification ? {
    type: notification.notificationSeverity as 'error' | 'warning',
    title: notification.notificationTitle,
    message: notification.notificationMessage
  } : null;

  // Show loading screen while locations are being fetched
  if (locationsLoading) {
    return <LoadingScreen message="Loading forecast data..." />;
  }

  // Show empty state when user has no location access
  if (locationsToShow.length === 0) {
    return <NoLocationsScreen />;
  }

  // Show loading screen while forecast data is being fetched
  if (timelineData.length === 0) {
    return <LoadingScreen message="Loading forecast data..." />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl">
        {/* pt: { sm: 3 } creates space below AppBar for sticky header to work properly */}
        <Box sx={{ my: { xs: 4, sm: 0 }, mb: { sm: 4 }, pt: { sm: 3 } }}>
          {/* NotificationBanner hidden - data quality warnings now shown in StatusAlert below divider */}
          {/* <NotificationBanner
            show={notification.showNotification}
            severity={notification.notificationSeverity}
            title={notification.notificationTitle}
            message={notification.notificationMessage}
          /> */}

          {/* Show updating chip when refreshing with existing data (not initial load) */}
          <UpdatingDataChip show={loading && timelineData.length > 0} />

          {/* Combined Timeline for all locations */}
          {timelineData.length > 0 && (
            <ForecastTimelineContainer
              timelineData={timelineData}
              title="Current Forecasts"
              customNow={customNow}
              useUTCTime={useUTCTime}
              onTimezoneToggle={() => setUseUTCTime(!useUTCTime)}
              onAdjustDate={() => {
                const url = new URL(window.location.href);
                if (showControls) {
                  url.searchParams.delete('showControls');
                  window.history.pushState({}, '', url.toString());
                  setShowControls(false);
                } else {
                  url.searchParams.set('showControls', 'true');
                  window.history.pushState({}, '', url.toString());
                  setShowControls(true);
                }
              }}
              hasDataError={nzHasDataError || ukEuHasDataError}
              dataQualityWarning={dataQualityWarning}
              fogStatus={fogStatus}
              isLoading={loading && timelineData.length === 0}
              timeNavigationControls={
                showControls ? (
                  <TimeNavigationControls
                    currentTime={customNow}
                    onTimeChange={updateUrlWithTime}
                    showControls={showControls}
                    useUTCTime={useUTCTime}
                  />
                ) : undefined
              }
              sx={{
                '& .MuiTypography-h5': {
                  fontSize: { xs: '1.25rem', sm: '1.75rem' }
                }
              }}
            />
          )}
          
          {/* Status bar with last updated time */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' },
              backgroundColor: 'rgba(50, 50, 50, 0.5)',
              borderRadius: 1,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1.5, sm: 1 },
              mb: 2,
              mt: 1,
              gap: { xs: 1, sm: 0 }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '1rem' }
              }}
            >
              {customNow ? (
                <>
                  Custom time: {customNow.toISOString()} 
                  <span style={{ 
                    color: '#f57c00',
                    display: 'block',
                    marginTop: '4px',
                    fontSize: 'inherit'
                  }}>
                    (auto-refresh disabled)
                  </span>
                </>
              ) : (
                <>Last updated: {formatLastUpdated(lastUpdated)}</>
              )}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!customNow && (
                <Box
                  component="button"
                  onClick={() => {
                    const url = new URL(window.location.href);
                    if (showControls) {
                      url.searchParams.delete('showControls');
                      window.history.pushState({}, '', url.toString());
                      setShowControls(false);
                    } else {
                      url.searchParams.set('showControls', 'true');
                      window.history.pushState({}, '', url.toString());
                      setShowControls(true);
                    }
                  }}
                  sx={{ 
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    border: 'none',
                    borderRadius: 1,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 0.5 },
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    alignSelf: { xs: 'flex-start', sm: 'auto' },
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    fontWeight: 500
                  }}
                >
                  Adjust Date
                </Box>
              )}
              {!customNow && (
                <Box
                  component="button"
                  onClick={refresh}
                  sx={{ 
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    border: 'none',
                    borderRadius: 1,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 0.5 },
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    alignSelf: { xs: 'flex-start', sm: 'auto' },
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    fontWeight: 500
                  }}
                >
                  Refresh Now
                </Box>
              )}
            </Box>
          </Box>
          
          {/* [LEAVE THIS BLOCK COMMENTED OUT, DO NOT REMOVE] */}
          {/* {isDebugMode && forecastLocations.length > 0 && ( */}
          {/*   <> */}
          {/*     <Divider sx={{ my: 4 }} /> */}
          {/*     <FogForecastDebug forecastLocations={forecastLocations.map(item => ({ */}
          {/*       locationId: item.locationId, */}
          {/*       locationName: item.location, */}
          {/*       forecast: item.forecast */}
          {/*     }))} /> */}
          {/*   </> */}
          {/* )} */}

          {error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, color: 'error.main' }}>
              {error}
            </Box>
          )}

        </Box>
      </Container>
      
      {/* Floating Feedback Button */}
      <Tooltip title="Let us know if something doesn't look right" placement="left">
        <Fab
          color="primary"
          aria-label="feedback"
          onClick={() => setFeedbackModalOpen(true)}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            backgroundColor: '#4dd0e1',
            '&:hover': {
              backgroundColor: '#3db9c7'
            },
            zIndex: 1000
          }}
        >
          <FeedbackIcon />
        </Fab>
      </Tooltip>
      
      {/* Feedback Modal */}
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />
    </LocalizationProvider>
  );
} 